import { EMAIL_UNVERIFIED_EVENT } from "./api.js";
import { getCurrentUser, needsEmailVerification, resendVerification } from "./auth.js";
import { esc } from "./utils.js";
import { ApiError } from "./api.js";

/** Two ways an unverified account learns what's wrong:
 *
 *  1. A standing banner above every view, so the limitation is visible
 *     before they bump into it.
 *  2. A modal the moment a request is actually refused, because a banner
 *     is easy to scroll past and the 403 is otherwise silent — the click
 *     just appears to do nothing.
 *
 * Both offer a resend, since "check your email" is useless advice if the
 * original message never arrived.
 */

let modalInstance = null;

function emailOf() {
  const user = getCurrentUser();
  return user?.email || "your email address";
}

async function doResend(buttonEl, statusEl) {
  buttonEl.disabled = true;
  statusEl.className = "small mt-2 text-slate";
  statusEl.textContent = "Sending...";
  try {
    await resendVerification();
    statusEl.className = "small mt-2 text-success";
    statusEl.textContent = `Sent — check ${emailOf()} for a fresh link.`;
  } catch (err) {
    statusEl.className = "small mt-2 text-danger";
    statusEl.textContent =
      err instanceof ApiError ? err.message : "Could not send a new link.";
    buttonEl.disabled = false;
  }
}

function ensureModal() {
  if (document.getElementById("verify-modal")) return;
  const el = document.createElement("div");
  el.innerHTML = `
    <div class="modal fade" id="verify-modal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title"><i class="bi bi-envelope-exclamation me-2"></i>Confirm your email first</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <p id="verify-modal-message" class="mb-2"></p>
            <p class="text-slate small mb-0">
              We sent a confirmation link to <span id="verify-modal-email" class="fw-semibold"></span>.
              Open it and you'll be able to post, comment, join cohorts, RSVP to dives,
              and upload photos. Browsing works either way.
            </p>
            <div id="verify-modal-status"></div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal">Close</button>
            <button type="button" class="btn btn-primary" id="verify-modal-resend">Resend the link</button>
          </div>
        </div>
      </div>
    </div>`;
  document.body.appendChild(el.firstElementChild);

  const modalEl = document.getElementById("verify-modal");
  modalInstance = new bootstrap.Modal(modalEl);
  document.getElementById("verify-modal-resend").addEventListener("click", (e) =>
    doResend(e.currentTarget, document.getElementById("verify-modal-status"))
  );
}

function showModal(message) {
  ensureModal();
  document.getElementById("verify-modal-message").textContent =
    message || "That action needs a confirmed email address.";
  document.getElementById("verify-modal-email").textContent = emailOf();
  const status = document.getElementById("verify-modal-status");
  status.textContent = "";
  status.className = "";
  document.getElementById("verify-modal-resend").disabled = false;
  modalInstance.show();
}

/** Standing banner, re-evaluated on every route render and auth change. */
export function renderVerificationBanner() {
  const existing = document.getElementById("verify-banner");
  if (!needsEmailVerification()) {
    if (existing) existing.remove();
    return;
  }
  if (existing) return; // already up; leave its resend state alone

  const banner = document.createElement("div");
  banner.id = "verify-banner";
  banner.className = "container pt-3";
  banner.innerHTML = `
    <div class="alert alert-warning d-flex flex-wrap align-items-center gap-2 mb-0" role="alert">
      <i class="bi bi-envelope-exclamation"></i>
      <div class="flex-grow-1">
        <span class="fw-semibold">Confirm your email to start posting.</span>
        <span class="d-block small">
          We sent a link to ${esc(emailOf())}. Until you open it you can browse, but not post,
          comment, join cohorts, RSVP, or upload photos.
        </span>
        <div id="verify-banner-status"></div>
      </div>
      <button class="btn btn-sm btn-warning" id="verify-banner-resend">Resend</button>
    </div>`;

  const main = document.getElementById("app");
  main.parentNode.insertBefore(banner, main);
  document.getElementById("verify-banner-resend").addEventListener("click", (e) =>
    doResend(e.currentTarget, document.getElementById("verify-banner-status"))
  );
}

/** Call once at boot. */
export function initVerificationNotices() {
  window.addEventListener(EMAIL_UNVERIFIED_EVENT, (e) => showModal(e.detail?.message));
}
